import { gql } from "@apollo/client";

export const CREATE_CREW_MEMBER = gql`
  mutation createCrewMember(
    $firstName: String!
    $lastName: String!
    $phone: String
    $email: String
    $street1: String
    $street2: String
    $city: String
    $state: String
    $zipcode: String
    $isForeman: Boolean!
    $classification: CrewMemberClassification!
    $userId: Int
    $emergencyContacts: [EmergencyContactInputType]
    $certifications: [CertificationInputType]
  ) {
    createCrewMember(
      firstName: $firstName
      lastName: $lastName
      phone: $phone
      email: $email
      street1: $street1
      street2: $street2
      city: $city
      state: $state
      zipcode: $zipcode
      isForeman: $isForeman
      classification: $classification
      userId: $userId
      emergencyContacts: $emergencyContacts
      certifications: $certifications
    ) {
      crewMember {
        id
        firstName
        lastName
        phone
        email
        street1
        street2
        city
        state
        zipcode
        isForeman
        isActive
        classification
        user {
          id
          username
        }
        emergencyContacts {
          id
          firstName
          lastName
          phone
          isPrimary
        }
        certifications {
          id
          name
          expirationDate
        }
      }
    }
  }
`;

export const EDIT_CREW_MEMBER = gql`
  mutation editCrewMember(
    $id: Int!
    $firstName: String!
    $lastName: String!
    $phone: String
    $email: String
    $street1: String
    $street2: String
    $city: String
    $state: String
    $zipcode: String
    $isForeman: Boolean!
    $isActive: Boolean!
    $classification: CrewMemberClassification!
    $userId: Int
    $emergencyContactsToAdd: [EmergencyContactInputType]
    $emergencyContactsToUpdate: [EditEmergencyContactInputType]
    $emergencyContactsToDelete: [Int]
    $certificationsToAdd: [CertificationInputType]
    $certificationsToUpdate: [EditCertificationInputType]
    $certificationsToDelete: [Int]
  ) {
    editCrewMember(
      id: $id
      firstName: $firstName
      lastName: $lastName
      phone: $phone
      email: $email
      street1: $street1
      street2: $street2
      city: $city
      state: $state
      zipcode: $zipcode
      isForeman: $isForeman
      isActive: $isActive
      classification: $classification
      userId: $userId
      emergencyContactsToAdd: $emergencyContactsToAdd
      emergencyContactsToUpdate: $emergencyContactsToUpdate
      emergencyContactsToDelete: $emergencyContactsToDelete
      certificationsToAdd: $certificationsToAdd
      certificationsToUpdate: $certificationsToUpdate
      certificationsToDelete: $certificationsToDelete
    ) {
      crewMember { 
        id 
        classification
        firstName
        lastName
        phone
        email
        street1
        street2
        city
        state
        zipcode
        isForeman
        isActive
        user {
          id
          username
        }
        emergencyContacts {
          id
          firstName
          lastName
          phone
          isPrimary
        }
        certifications {
          id
          name
          expirationDate
        }
      }
    }
  }
`;

export const DELETE_CREW_MEMBER = gql`
  mutation deleteCrewMember($id: Int!) {
    deleteCrewMember(id: $id) {
      didDelete
      message
    }
  }
`;


// Emergency Contact Mutations
export const CREATE_EMERGENCY_CONTACT = gql`
  mutation createEmergencyContact(
    $firstName: String!
    $lastName: String!
    $phone: String!
    $isPrimary: Boolean!
    $crewMember: Int!
  ) {
    createEmergencyContact(
      firstName: $firstName
      lastName: $lastName
      phone: $phone
      isPrimary: $isPrimary
      crewMember: $crewMember
    ) {
      emergencyContact {
        id
        firstName
        lastName
        phone
        isPrimary
      }
    }
  }
`;

export const UPDATE_EMERGENCY_CONTACT = gql`
  mutation updateEmergencyContact(
    $id: Int!
    $firstName: String!
    $lastName: String!
    $phone: String!
    $isPrimary: Boolean!
  ) {
    updateEmergencyContact(
      id: $id
      firstName: $firstName
      lastName: $lastName
      phone: $phone
      isPrimary: $isPrimary
    ) {
      emergencyContact {
        id
        firstName
        lastName
        phone
        isPrimary
      }
    }
  }
`;

export const DELETE_EMERGENCY_CONTACT = gql`
  mutation deleteEmergencyContact($id: Int!) {
    deleteEmergencyContact(id: $id) {
      success
      message
    }
  }
`;

// Certification Mutations
export const CREATE_CERTIFICATION = gql`
  mutation createCertification(
    $name: String!
    $expirationDate: Date!
    $crewMember: Int!
  ) {
    createCertification(
      name: $name
      expirationDate: $expirationDate
      crewMember: $crewMember
    ) {
      certification {
        id
        name
        expirationDate
      }
    }
  }
`;

export const UPDATE_CERTIFICATION = gql`
  mutation updateCertification(
    $id: Int!
    $name: String!
    $expirationDate: Date!
  ) {
    updateCertification(
      id: $id
      name: $name
      expirationDate: $expirationDate
    ) {
      certification {
        id
        name
        expirationDate
      }
    }
  }
`;

export const DELETE_CERTIFICATION = gql`
  mutation deleteCertification($id: Int!) {
    deleteCertification(id: $id) {
      success
      message
    }
  }
`;

// Crew Member Assignment Mutations
export const CREATE_CREW_MEMBER_ASSIGNMENT = gql`
  mutation createCrewMemberAssignment(
    $crewMember: Int!
    $jobDay: Int!
    $startTime: Time!
    $endTime: Time
    $safetyAcknowledgment: Boolean!
  ) {
    createCrewMemberAssignment(
      crewMember: $crewMember
      jobDay: $jobDay
      startTime: $startTime
      endTime: $endTime
      safetyAcknowledgment: $safetyAcknowledgment
    ) {
      crewMemberAssignment {
        id
        startTime
        endTime
        safetyAcknowledgment
        crewMember {
          id
          firstName
          lastName
        }
        jobDay {
          id
          date
        }
      }
    }
  }
`;

export const UPDATE_CREW_MEMBER_ASSIGNMENT = gql`
  mutation updateCrewMemberAssignment(
    $id: Int!
    $startTime: Time!
    $endTime: Time
    $safetyAcknowledgment: Boolean!
  ) {
    updateCrewMemberAssignment(
      id: $id
      startTime: $startTime
      endTime: $endTime
      safetyAcknowledgment: $safetyAcknowledgment
    ) {
      crewMemberAssignment {
        id
        startTime
        endTime
        safetyAcknowledgment
      }
    }
  }
`;

export const DELETE_CREW_MEMBER_ASSIGNMENT = gql`
  mutation deleteCrewMemberAssignment($id: Int!) {
    deleteCrewMemberAssignment(id: $id) {
      success
      message
    }
  }
`;

// Assignment SignOff Mutations
export const CREATE_ASSIGNMENT_SIGN_OFF = gql`
  mutation createAssignmentSignOff(
    $assignment: Int!
    $signature: Upload!
  ) {
    createAssignmentSignOff(
      assignment: $assignment
      signature: $signature
    ) {
      assignmentSignOff {
        id
        signature
        assignment {
          id
        }
      }
    }
  }
`;

export const UPDATE_ASSIGNMENT_SIGN_OFF = gql`
  mutation updateAssignmentSignOff(
    $id: Int!
    $signature: Upload!
  ) {
    updateAssignmentSignOff(
      id: $id
      signature: $signature
    ) {
      assignmentSignOff {
        id
        signature
      }
    }
  }
`;

export const DELETE_ASSIGNMENT_SIGN_OFF = gql`
  mutation deleteAssignmentSignOff($id: Int!) {
    deleteAssignmentSignOff(id: $id) {
      success
      message
    }
  }
`;
